import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  ImageIcon, 
  Upload, 
  Download, 
  RefreshCw, 
  ArrowLeft, 
  Sliders, 
  Layers, 
  Maximize2, 
  Lock, 
  Check, 
  AlertCircle, 
  ChevronRight,
  Info
} from 'lucide-react';

export const ImageCompressor = () => {
  const { withAd, toast } = useAdRedirect();
  const [imageFile, setImageFile] = useState(null);
  const [originalUrl, setOriginalUrl] = useState('');
  const [compressedUrl, setCompressedUrl] = useState('');
  
  // Image metadata
  const [meta, setMeta] = useState({
    name: '',
    originalSize: 0,
    compressedSize: 0,
    originalWidth: 0,
    originalHeight: 0,
    compressedWidth: 0,
    compressedHeight: 0,
    format: '',
    savings: 0
  });

  // Compression controls
  const [quality, setQuality] = useState(80); // 10 to 100
  const [scale, setScale] = useState(100); // 10 to 100
  const [format, setFormat] = useState('webp'); // webp, jpeg, original

  // UI state
  const [isProcessing, setIsProcessing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [isDownloaded, setIsDownloaded] = useState(false);
  
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);

  // Clean up ObjectURLs to avoid memory leaks
  useEffect(() => {
    return () => {
      if (originalUrl) URL.revokeObjectURL(originalUrl);
      if (compressedUrl) URL.revokeObjectURL(compressedUrl);
    };
  }, [originalUrl, compressedUrl]);

  // Handle Drag & Drop actions
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  // Read metadata and setup previews
  const processFile = (file) => {
    if (!file.type.startsWith('image/')) {
      alert('Unsupported file format. Please drop a valid image file (PNG, JPG, WebP).');
      return;
    }

    const prevUrl = URL.createObjectURL(file);
    setOriginalUrl(prevUrl);
    setImageFile(file);
    setIsDownloaded(false);

    // Read image dimensions
    const img = new Image();
    img.onload = () => {
      setMeta({
        name: file.name,
        originalSize: file.size,
        compressedSize: 0,
        originalWidth: img.naturalWidth,
        originalHeight: img.naturalHeight,
        compressedWidth: img.naturalWidth,
        compressedHeight: img.naturalHeight,
        format: file.type.split('/')[1] || 'png',
        savings: 0
      });
    };
    img.src = prevUrl;
  };

  // Core local canvas compression loop
  const compressImage = () => {
    if (!imageFile || !originalUrl) return;

    setIsProcessing(true);
    const img = new Image();
    
    img.onload = () => {
      const canvas = canvasRef.current || document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        setIsProcessing(false);
        return;
      }

      // 1. Calculate scaled dimensions
      const width = Math.round(img.naturalWidth * (scale / 100));
      const height = Math.round(img.naturalHeight * (scale / 100));
      
      canvas.width = width;
      canvas.height = height;

      // Draw image onto clean canvas
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      // 2. Select output format mime-type
      let targetMime = 'image/webp';
      let extension = 'webp';

      if (format === 'jpeg') {
        targetMime = 'image/jpeg';
        extension = 'jpg';
      } else if (format === 'original') {
        const origType = imageFile.type;
        targetMime = origType === 'image/png' ? 'image/png' : origType; // PNG fallback
        extension = origType.split('/')[1] || 'png';
      }

      // 3. Compress using toBlob
      const qualityFactor = quality / 100;
      
      canvas.toBlob((blob) => {
        if (!blob) {
          setIsProcessing(false);
          return;
        }

        if (compressedUrl) {
          URL.revokeObjectURL(compressedUrl);
        }

        const newUrl = URL.createObjectURL(blob);
        setCompressedUrl(newUrl);

        // Update compression ratios
        const savings = Math.max(0, Math.round(((meta.originalSize - blob.size) / meta.originalSize) * 100));

        setMeta(prev => ({
          ...prev,
          compressedSize: blob.size,
          compressedWidth: width,
          compressedHeight: height,
          savings: savings
        }));

        setIsProcessing(false);
      }, targetMime, qualityFactor);
    };

    img.src = originalUrl;
  };

  // Re-run compression dynamically when controls change
  useEffect(() => {
    if (imageFile) {
      const timer = setTimeout(() => {
        compressImage();
      }, 150); // debounce to keep sliders extremely responsive
      return () => clearTimeout(timer);
    }
  }, [imageFile, quality, scale, format]);

  // Utility to format sizes beautifully
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Download trigger
  const handleDownload = () => {
    if (!compressedUrl) return;

    const link = document.createElement('a');
    
    // Formulate a beautiful output name
    const origBaseName = meta.name.substring(0, meta.name.lastIndexOf('.')) || meta.name;
    const targetExtension = format === 'original' ? meta.format : (format === 'jpeg' ? 'jpg' : format);
    link.download = `${origBaseName}-compressed.${targetExtension}`;
    link.href = compressedUrl;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setIsDownloaded(true);
    setTimeout(() => setIsDownloaded(false), 3000);
  };

  const handleReset = () => {
    setImageFile(null);
    setOriginalUrl('');
    setCompressedUrl('');
    setMeta({
      name: '',
      originalSize: 0,
      compressedSize: 0,
      originalWidth: 0,
      originalHeight: 0,
      compressedWidth: 0,
      compressedHeight: 0,
      format: '',
      savings: 0
    });
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%',
      paddingBottom: '60px'
    }}>
      <SEO 
        title="Image Compressor - Compress Image Without Losing Quality" 
        description="Free image compressor and photo compressor. Compress image online, reduce image size instantly. Compress image without losing quality using client-side tech."
        keywords="image compressor, compress image online, reduce image size, photo compressor, compress image without losing quality"
      />
      
      {/* Hidden canvas utility */}
      <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>

      {/* Breadcrumbs Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '24px',
        fontSize: '0.9rem'
      }}>
        <Link to="/" style={{
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'var(--transition-smooth)'
        }} className="breadcrumb-back">
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Smart Image Compressor</span>
      </div>



      {/* Main Compressor Content */}
      {!imageFile ? (
        /* Drag & Drop Upload State */
        <div 
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: dragActive ? '2.5px dashed var(--primary)' : '2px dashed var(--border-color)',
            background: dragActive ? 'rgba(5, 117, 251, 0.02)' : '#ffffff',
            borderRadius: '24px',
            padding: '80px 40px',
            textAlign: 'center',
            cursor: 'pointer',
            boxShadow: dragActive ? 'var(--shadow-md)' : 'var(--shadow-sm)',
            transition: 'var(--transition-smooth)',
            maxWidth: '800px',
            margin: '0 auto'
          }}
          className="drag-drop-compressor"
        >
          <input 
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/png, image/jpeg, image/jpg, image/webp"
            style={{ display: 'none' }}
          />

          <div style={{
            background: 'rgba(5, 117, 251, 0.05)',
            color: 'var(--primary)',
            width: '68px',
            height: '68px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px auto'
          }}>
            <Upload size={32} />
          </div>

          <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
            Drag and Drop Image File here
          </h3>
          <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', maxWidth: '420px', margin: '0 auto 20px auto', lineHeight: 1.5 }}>
            Supports JPG, JPEG, PNG, or WebP images. Maximum recommended file size 25MB.
          </p>
          <button style={{
            background: 'var(--primary)',
            color: '#ffffff',
            border: 'none',
            borderRadius: '10px',
            padding: '12px 24px',
            fontSize: '0.9rem',
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'var(--transition-smooth)',
            boxShadow: '0 4px 12px -2px rgba(5, 117, 251, 0.25)'
          }} className="browse-files-btn">
            Browse Computer Files
          </button>
        </div>
      ) : (
        /* Workspace state (Image is loaded) */
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '32px',
          alignItems: 'start'
        }} className="compressor-workspace-grid">
          
          {/* Left Column: Comparison Panels */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
            
            {/* Visual Side by Side Comparison Row */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '24px'
            }}>
              {/* Before Card */}
              <div style={cardStyle}>
                <div style={cardHeaderStyle}>
                  <span style={headerLabelStyle}>Original Image (Before)</span>
                  <span style={{ ...badgeStyle, background: 'rgba(15, 23, 42, 0.06)', color: 'var(--text-secondary)' }}>
                    Original Format
                  </span>
                </div>
                <div style={imgFrameStyle}>
                  <img src={originalUrl} alt="Original input source" style={previewImgStyle} />
                </div>
                <div style={metaFooterStyle}>
                  <div style={metaSubRowStyle}>
                    <span style={metaLabelStyle}>File Name:</span>
                    <span style={metaValStyle} title={meta.name}>{meta.name}</span>
                  </div>
                  <div style={metaSubRowStyle}>
                    <span style={metaLabelStyle}>Dimensions:</span>
                    <span style={metaValStyle}>{meta.originalWidth} &times; {meta.originalHeight} px</span>
                  </div>
                  <div style={metaSubRowStyle}>
                    <span style={metaLabelStyle}>File Size:</span>
                    <span style={{ ...metaValStyle, color: '#ef4444', fontWeight: 800 }}>{formatBytes(meta.originalSize)}</span>
                  </div>
                </div>
              </div>

              {/* After Card */}
              <div style={{ ...cardStyle, border: '1px solid rgba(16, 185, 129, 0.25)', boxShadow: '0 10px 30px -12px rgba(16, 185, 129, 0.1)' }}>
                <div style={cardHeaderStyle}>
                  <span style={headerLabelStyle}>Optimized Image (After)</span>
                  {meta.savings > 0 && (
                    <span style={{ ...badgeStyle, background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', fontWeight: 800 }}>
                      Saved {meta.savings}%
                    </span>
                  )}
                </div>
                <div style={imgFrameStyle}>
                  {isProcessing ? (
                    <div style={loadingOverlayStyle}>
                      <RefreshCw size={28} className="spinner" style={{ color: 'var(--primary)' }} />
                      <p style={{ fontSize: '0.8rem', fontWeight: 700, margin: '8px 0 0 0', color: 'var(--text-secondary)' }}>Optimizing assets...</p>
                    </div>
                  ) : (
                    <img src={compressedUrl || originalUrl} alt="Optimized compressed output" style={previewImgStyle} />
                  )}
                </div>
                <div style={metaFooterStyle}>
                  <div style={metaSubRowStyle}>
                    <span style={metaLabelStyle}>Format Output:</span>
                    <span style={metaValStyle}>{format === 'original' ? meta.format.toUpperCase() : format.toUpperCase()}</span>
                  </div>
                  <div style={metaSubRowStyle}>
                    <span style={metaLabelStyle}>Dimensions:</span>
                    <span style={metaValStyle}>{meta.compressedWidth} &times; {meta.compressedHeight} px</span>
                  </div>
                  <div style={metaSubRowStyle}>
                    <span style={metaLabelStyle}>File Size:</span>
                    <span style={{ ...metaValStyle, color: '#10b981', fontWeight: 800 }}>
                      {isProcessing ? 'Calculating...' : formatBytes(meta.compressedSize)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Live compression statistics summary banner */}
            <div style={{
              background: 'radial-gradient(ellipse at 80% 20%, rgba(16, 185, 129, 0.04) 0%, transparent 60%), #ffffff',
              border: '1px solid rgba(16, 185, 129, 0.18)',
              borderRadius: '16px',
              padding: '24px',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  background: 'rgba(16, 185, 129, 0.1)',
                  color: '#10b981',
                  padding: '10px',
                  borderRadius: '10px',
                  display: 'inline-flex'
                }}>
                  <Check size={20} />
                </div>
                <div>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: '1rem', fontWeight: 800, color: 'var(--text-primary)' }}>Compression Process Complete</h4>
                  <p style={{ margin: 0, fontSize: '0.825rem', color: 'var(--text-secondary)' }}>
                    Client-side algorithm successfully compressed your asset in real-time.
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '28px' }}>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Weight Reduction</span>
                  <p style={{ margin: 0, fontSize: '1.4rem', fontWeight: 900, color: '#10b981' }}>
                    {meta.savings > 0 ? `-${meta.savings}%` : '0%'}
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Bytes Saved</span>
                  <p style={{ margin: 0, fontSize: '1.4rem', fontWeight: 900, color: 'var(--text-primary)' }}>
                    {formatBytes(Math.max(0, meta.originalSize - meta.compressedSize))}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Compression parameters control card */}
          <div style={{
            position: 'sticky',
            top: '110px',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            width: '100%',
            maxWidth: '380px',
            margin: '0 auto'
          }} className="compressor-control-column">
            
            {/* Compression configuration parameters */}
            <div style={{
              background: '#ffffff',
              border: '1px solid var(--border-color)',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: 'var(--shadow-md)'
            }}>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Sliders size={18} style={{ color: 'var(--primary)' }} /> Optimization Controls
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                
                {/* 1. Format Selector Options */}
                <div style={inputGroupStyle}>
                  <label style={labelStyle}>Target Format</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
                    {['webp', 'jpeg', 'original'].map((fmt) => (
                      <button
                        key={fmt}
                        onClick={() => setFormat(fmt)}
                        style={{
                          height: '38px',
                          border: format === fmt ? '1.5px solid var(--primary)' : '1px solid var(--border-color)',
                          borderRadius: '8px',
                          fontSize: '0.78rem',
                          fontWeight: 800,
                          cursor: 'pointer',
                          background: format === fmt ? 'rgba(5, 117, 251, 0.05)' : '#ffffff',
                          color: format === fmt ? 'var(--primary)' : 'var(--text-secondary)',
                          textTransform: 'uppercase',
                          transition: 'var(--transition-smooth)'
                        }}
                      >
                        {fmt === 'original' ? 'Keep Org' : fmt}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Info Tip about PNG */}
                {format === 'original' && meta.format === 'png' && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                    background: 'rgba(14, 165, 233, 0.04)',
                    border: '1px solid rgba(14, 165, 233, 0.12)',
                    borderRadius: '8px',
                    padding: '10px 12px'
                  }}>
                    <Info size={14} style={{ color: 'var(--secondary)', flexShrink: 0, marginTop: '2px' }} />
                    <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.4 }}>
                      PNG is a lossless format and quality adjustments have no effect. Convert to <strong>WebP</strong> for up to 90% size reduction!
                    </p>
                  </div>
                )}

                {/* 2. Compression Quality Slider */}
                <div style={inputGroupStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={labelStyle}>Compression Quality</label>
                    <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--primary)', background: 'rgba(5, 117, 251, 0.06)', padding: '2px 8px', borderRadius: '6px' }}>
                      {quality}%
                    </span>
                  </div>
                  <input 
                    type="range"
                    min="10"
                    max="100"
                    step="1"
                    value={quality}
                    onChange={(e) => setQuality(parseInt(e.target.value))}
                    disabled={format === 'original' && meta.format === 'png'}
                    style={{
                      width: '100%',
                      accentColor: 'var(--primary)',
                      cursor: (format === 'original' && meta.format === 'png') ? 'not-allowed' : 'pointer',
                      opacity: (format === 'original' && meta.format === 'png') ? 0.5 : 1
                    }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                    <span>Max Compression</span>
                    <span>Best Quality</span>
                  </div>
                </div>

                {/* 3. Image Dimensions Scale Slider */}
                <div style={inputGroupStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={labelStyle}>Dimension Scale</label>
                    <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--secondary)', background: 'rgba(14, 165, 233, 0.06)', padding: '2px 8px', borderRadius: '6px' }}>
                      {scale}%
                    </span>
                  </div>
                  <input 
                    type="range"
                    min="10"
                    max="100"
                    step="5"
                    value={scale}
                    onChange={(e) => setScale(parseInt(e.target.value))}
                    style={{ width: '100%', accentColor: 'var(--secondary)', cursor: 'pointer' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                    <span>Downscaled ({Math.round(meta.originalWidth * 0.1)}px)</span>
                    <span>Full Scale ({meta.originalWidth}px)</span>
                  </div>
                </div>

                <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)', margin: '4px 0' }} />

                {/* Primary Action Buttons */}
                <AdToast message={toast} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <button
                    onClick={withAd('img-compress', handleDownload)}
                    disabled={isProcessing || !compressedUrl}
                    style={{
                      background: isDownloaded ? '#059669' : 'linear-gradient(90deg, var(--secondary) 0%, var(--primary) 100%)',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '14px',
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      cursor: (isProcessing || !compressedUrl) ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 6px 18px -2px rgba(5, 117, 251, 0.3)',
                      transition: 'var(--transition-smooth)'
                    }}
                    className="download-compressed-btn"
                  >
                    {isDownloaded ? <Check size={18} /> : <Download size={18} />}
                    {isDownloaded ? 'Downloaded Successfully!' : 'Download Compressed Image'}
                  </button>

                  <button
                    onClick={handleReset}
                    style={{
                      background: 'rgba(15, 23, 42, 0.03)',
                      color: 'var(--text-primary)',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '12px',
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'var(--transition-smooth)'
                    }}
                    className="reset-compressor-btn"
                  >
                    <RefreshCw size={14} /> Compress Another Image
                  </button>
                </div>

              </div>
            </div>



          </div>
        </div>
      )}

      {/* Workspace design styling injections */}
      <style>{`
        @media (min-width: 992px) {
          .compressor-workspace-grid {
            grid-template-columns: 1fr 380px !important;
          }
        }

        .drag-drop-compressor {
          transition: transform 0.25s, box-shadow 0.25s, border-color 0.25s !important;
        }
        .drag-drop-compressor:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md) !important;
          border-color: var(--primary) !important;
        }

        .browse-files-btn:hover {
          background: var(--primary-hover) !important;
          transform: translateY(-1px);
          box-shadow: 0 6px 16px -2px rgba(5, 117, 251, 0.4) !important;
        }

        .download-compressed-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 8px 20px -2px rgba(5, 117, 251, 0.45) !important;
          opacity: 0.95;
        }

        .reset-compressor-btn:hover {
          background: rgba(15, 23, 42, 0.06) !important;
        }

        .spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* SEO: FAQ & Related Tools */}
      <AdUnit />
      <ToolPageExtras toolId="image-compressor" />
    </div>
  );
};

// Premium structural inline styles
const cardStyle = {
  background: '#ffffff',
  border: '1px solid var(--border-color)',
  borderRadius: '20px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  boxShadow: 'var(--shadow-sm)'
};

const cardHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

const headerLabelStyle = {
  fontSize: '0.85rem',
  fontWeight: 800,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

const badgeStyle = {
  fontSize: '0.725rem',
  padding: '4px 10px',
  borderRadius: '99px',
  fontWeight: 700
};

const imgFrameStyle = {
  background: 'repeating-conic-gradient(#f8fafc 0% 25%, #f1f5f9 0% 50%) 50% / 20px 20px', // transparent checkerboard layout
  border: '1px solid var(--border-color)',
  borderRadius: '12px',
  aspectRatio: '16/10',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  position: 'relative'
};

const previewImgStyle = {
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain'
};

const loadingOverlayStyle = {
  position: 'absolute',
  inset: 0,
  background: 'rgba(255, 255, 255, 0.85)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center'
};

const metaFooterStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  paddingTop: '8px',
  borderTop: '1px solid var(--border-color)'
};

const metaSubRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: '0.85rem'
};

const metaLabelStyle = {
  color: 'var(--text-muted)',
  fontWeight: 600
};

const metaValStyle = {
  color: 'var(--text-primary)',
  fontWeight: 700,
  maxWidth: '180px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const inputGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
};

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: 800,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

export default ImageCompressor;
